import { DETAIL_PANEL_WIDTH } from '../../theme/layout';

/** Pixels the docked panels and top chrome cover on each edge of the map. */
export interface ClearViewInsets {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface Size {
  x: number;
  y: number;
}

export const NO_INSETS: ClearViewInsets = { left: 0, right: 0, top: 0, bottom: 0 };

/**
 * Insets never exceed the map itself. When two opposite insets together
 * overrun the size (a phone-width window with a 420px dock), both shrink in
 * proportion so the clear rect stays a valid, if thin, strip.
 */
function fitInsets(size: Size, raw: ClearViewInsets): ClearViewInsets {
  const axis = (a: number, b: number, total: number): [number, number] => {
    const lo = Math.max(0, a);
    const hi = Math.max(0, b);
    const sum = lo + hi;
    if (sum <= total || sum === 0) return [lo, hi];
    const k = total / sum;
    return [lo * k, hi * k];
  };
  const [left, right] = axis(raw.left, raw.right, size.x);
  const [top, bottom] = axis(raw.top, raw.bottom, size.y);
  return { left, right, top, bottom };
}

/** Center of the part of the map the insets leave clear, in container pixels. */
export function clearViewCenter(size: Size, insets: ClearViewInsets): Point {
  const i = fitInsets(size, insets);
  return {
    x: i.left + (size.x - i.left - i.right) / 2,
    y: i.top + (size.y - i.top - i.bottom) / 2,
  };
}

/**
 * How far the map's own center must sit from a target so that the target
 * lands at the center of the clear rect. Add it to the target's projected
 * pixel position, unproject, and hand that to setView/flyTo: a plain
 * center-on-target would leave the target under a dock.
 *
 * Positive x means the map center sits right of the target (the target
 * appears left of the viewport center, as it does with a right dock).
 */
export function clearViewShift(size: Size, insets: ClearViewInsets): Point {
  const c = clearViewCenter(size, insets);
  return { x: size.x / 2 - c.x, y: size.y / 2 - c.y };
}

/**
 * Smallest pan (in container pixels, to add to the map's pan offset) that
 * brings `point` inside the clear rect, keeping `margin` px from each clear
 * edge. `{0,0}` when the point is already in clear view, which is the case
 * that must leave the camera alone.
 */
export function panIntoClearView(
  size: Size,
  insets: ClearViewInsets,
  point: Point,
  margin = 0
): Point {
  const i = fitInsets(size, insets);
  const axis = (p: number, lo: number, hi: number): number => {
    // Thin clear strip: aim for its middle instead of fighting both edges.
    if (hi - lo <= 2 * margin) return p - (lo + hi) / 2;
    if (p < lo + margin) return p - (lo + margin);
    if (p > hi - margin) return p - (hi - margin);
    return 0;
  };
  return {
    x: axis(point.x, i.left, size.x - i.right),
    y: axis(point.y, i.top, size.y - i.bottom),
  };
}

export function isInClearView(
  size: Size,
  insets: ClearViewInsets,
  point: Point,
  margin = 0
): boolean {
  const d = panIntoClearView(size, insets, point, margin);
  return d.x === 0 && d.y === 0;
}

/**
 * Fly only for a short hop: the new center within `maxScreens` screens of
 * the current one (measured at the destination zoom) and at most
 * `maxZoomDelta` zoom levels apart. A longer trip would blur through
 * unloaded tiles, so it jumps instead.
 */
export function shouldFly(
  distancePx: number,
  size: Size,
  zoomDelta: number,
  maxScreens = 2,
  maxZoomDelta = 3
): boolean {
  return Math.abs(zoomDelta) <= maxZoomDelta && distancePx <= maxScreens * Math.max(size.x, size.y);
}

/**
 * Insets to aim a structure focus at. Picking a structure closes any
 * left-docked plan panel and opens the structure panel on the right of the
 * desktop shell (DETAIL_PANEL_WIDTH), so those are the insets the map will
 * have once the 220ms dock slide finishes, even though nothing has
 * registered in uiStore yet when the camera starts to move. Off the desktop
 * shell the detail is a bottom sheet and the map keeps its full width.
 */
export function structureFocusInsets(isDesktopShell: boolean): Partial<ClearViewInsets> {
  return { left: 0, right: isDesktopShell ? DETAIL_PANEL_WIDTH : 0 };
}
